import React, { useState } from "react";
import {
  requestPasswordReset,
  resetPassword,
} from "../repository/authRepository";

type Step = "email" | "code" | "newPassword";

interface ForgotPasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await requestPasswordReset(email);
      setMessage(result.message);
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Code must be 6 digits");
      return;
    }
    setError(null);
    setStep("newPassword");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, code, newPassword);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: "400px", margin: "2rem auto" }}>
      <h2 style={{ marginBottom: "1.5rem" }}>Reset Password</h2>

      {error && (
        <div className="error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      {message && (
        <div
          style={{
            padding: "0.75rem",
            backgroundColor: "rgba(34, 139, 34, 0.1)",
            border: "1px solid var(--color-success)",
            borderRadius: "var(--radius-md)",
            color: "var(--color-success)",
            marginBottom: "1rem",
          }}
        >
          {message}
        </div>
      )}

      {step === "email" && (
        <form onSubmit={handleRequestCode}>
          <p style={{ marginBottom: "1rem", color: "var(--color-text-secondary)" }}>
            Enter your email address to receive a password reset code.
          </p>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%", marginTop: "0.5rem" }}
            disabled={loading}
          >
            {loading ? "..." : "Send Reset Code"}
          </button>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={handleVerifyCode}>
          <p style={{ marginBottom: "1rem", color: "var(--color-text-secondary)" }}>
            Enter the 6-digit code sent to your email.
          </p>
          <div className="form-group">
            <label className="form-label">Reset Code</label>
            <input
              type="text"
              className="form-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              maxLength={6}
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              style={{ letterSpacing: "0.5em", textAlign: "center" }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%", marginTop: "0.5rem" }}
            disabled={code.length !== 6}
          >
            Continue
          </button>
        </form>
      )}

      {step === "newPassword" && (
        <form onSubmit={handleResetPassword}>
          <p style={{ marginBottom: "1rem", color: "var(--color-text-secondary)" }}>
            Enter your new password.
          </p>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password"
              className="form-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%", marginTop: "0.5rem" }}
            disabled={loading}
          >
            {loading ? "..." : "Reset Password"}
          </button>
        </form>
      )}

      <p style={{ marginTop: "1rem", textAlign: "center" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: "var(--color-primary)",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Back to Login
        </button>
      </p>
    </div>
  );
};

export default ForgotPassword;
